// The Quick view (spec C7.3, the QuickView card): the right slide-over a row click opens. In the
// spec's order: the needs-attention line, details, steps, prior term vs renewal, notes, files and the
// latest activity (the last 3 lines of the Portal log); a handoff adds its reason, who sent it, the
// Act by and the VA's note. Footer: Reassign and Hand to AE, then Open record; for the AE a handoff
// waiting on them has Send back, More, Open record and Accept, one with them More, Open record and
// Finish handoff (spec C7.3, the QuickView card).
import { branchTag } from './branch-lines';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import type { Api, FetchView, IncreaseRule, LogEntry, RenewalRow, StepsView } from '../auth/api';
import type { Role } from '../roles';
import { Tip } from '../shell/Tip';
import { usePopover } from '../shell/use-popover';
import { Viewer, type ViewerFile } from '../shell/Viewer';
import { NoteLine } from './notes';
import { useNotes } from './use-notes';
import {
  AlertTriangleIcon,
  CheckIcon,
  ChevronDownIcon,
  DownloadIcon,
  FileIcon,
  SendIcon,
  SwapIcon,
  UndoIcon,
  UserCheckIcon,
  XIcon,
} from '../shell/icons';
import {
  actByChip,
  aeBannerTitle,
  reasonLong,
  reasonPillCls,
  resultText,
} from './ae-handoff-lines';
import { decStamp, docLabel } from './fetch-lines';
import { FollowUpFooter } from './FollowUp';
import { AwaitingTag } from './HandToAe';
import { offersHand, worksRow } from './handoff-lines';
import { LogLine, QuickSteps } from './RecordExtras';
import { isTopLayer, useModalLayer } from '../shell/modal-layer';
import {
  attentionBanner,
  band,
  change,
  daysLabel,
  daysPill,
  increaseTagText,
  increaseTooltip,
  longDate,
  money,
  showIncreaseTag,
  statuses,
  when,
  type Page,
  type TabKey,
} from './renewal-lines';

type Props = {
  api: Api;
  role: Role;
  /** The signed-in person: a Renewal VA acts only on the renewals they handle. */
  meId: string;
  row: RenewalRow;
  onReassign: () => void;
  /** Hand to AE (spec C6): opens the dialog. */
  onHand: () => void;
  /** Opens the Add note popup. */
  onAddNote: () => void;
  /** Accept and Send back, while the handoff waits on this AE; null otherwise. */
  onAccept: (() => void) | null;
  onSendBack: (() => void) | null;
  /** Follow up on, for the AE a handoff is with: true once saved, else why not. */
  onFollowUp: (handoffId: string, on: string | null) => Promise<true | string>;
  /** Finish handoff, for the AE a handoff is with (needs the steps read); null otherwise. */
  onFinish: ((steps: StepsView) => void) | null;
  /** Pass to another AE, a handoff on this AE (S15 batch 7); null otherwise. */
  onPass?: (() => void) | null;
  /** Take over (the AE, decision 1): a confirm; null where they may not. */
  onTakeOver?: (() => void) | null;
  /** Goes up each time a note is added or taken back, so the Notes section reads them again. */
  notesVersion: number;
  page: Page;
  /** The list tab the row was opened from, for the Record's breadcrumb; null with a stat filter. */
  tab: TabKey | null;
  now: Date;
  /** The stored increase rule, for the Change line's amber. */
  rule: IncreaseRule;
  onClose: () => void;
};

function Line({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="row between t13">
      <span className="muted">{label}</span>
      <span className={mono ? 'mono ink' : 'ink'}>{value}</span>
    </div>
  );
}

/** What the Quick view reads for one renewal; null until it arrives, "failed" when it could not. */
type Loaded = {
  id: string;
  steps: StepsView | null | 'failed';
  fetch: FetchView | null | 'failed';
  log: LogEntry[] | null | 'failed';
};

export function RenewalQuickView({
  api,
  role,
  meId,
  row,
  onReassign,
  onHand,
  onAddNote,
  onAccept,
  onSendBack,
  onFollowUp,
  onFinish,
  onPass = null,
  onTakeOver = null,
  notesVersion,
  page,
  tab,
  now,
  rule,
  onClose,
}: Props) {
  const notes = useNotes(api, row.id, notesVersion);
  const ref = useRef<HTMLElement>(null);
  const [loaded, setLoaded] = useState<Loaded>({ id: row.id, steps: null, fetch: null, log: null });
  const [file, setFile] = useState<ViewerFile | null>(null);
  // Follow up on's box over the footer (the Renewals board), for the row it was opened on.
  const [followingUp, setFollowingUp] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    const set = (part: Partial<Loaded>) =>
      live &&
      setLoaded((l) =>
        l.id === row.id
          ? { ...l, ...part }
          : { id: row.id, steps: null, fetch: null, log: null, ...part },
      );
    api.steps(row.id).then(
      (steps) => set({ steps }),
      () => set({ steps: 'failed' }),
    );
    api.fetchView(row.id).then(
      (fetch) => set({ fetch }),
      () => set({ fetch: 'failed' }),
    );
    api.renewalLog(row.id).then(
      (log) => set({ log }),
      () => set({ log: 'failed' }),
    );
    return () => {
      live = false;
    };
    // A new handler (Reassign or its Undo) reads them again, the Portal log with its new line; what
    // is shown stays until each answer arrives.
  }, [api, row.id, row.handledBy?.id]);
  // A note added or taken back: Latest activity reads the Portal log again, with its new line.
  const seenNotes = useRef(notesVersion);
  useEffect(() => {
    if (seenNotes.current === notesVersion) return;
    seenNotes.current = notesVersion;
    let live = true;
    api.renewalLog(row.id).then(
      (log) => live && setLoaded((l) => (l.id === row.id ? { ...l, log } : l)),
      () => {},
    );
    return () => {
      live = false;
    };
  }, [api, row.id, notesVersion]);
  const mine = loaded.id === row.id ? loaded : { steps: null, fetch: null, log: null };
  const steps = mine.steps && mine.steps !== 'failed' ? mine.steps : null;
  const fetch = mine.fetch && mine.fetch !== 'failed' ? mine.fetch : null;
  const log = mine.log && mine.log !== 'failed' ? mine.log : null;
  const closed = statuses[row.status].stage === 'closed';
  // A Renewal VA works only what they handle; one with an AE is read only for them (spec C6).
  const works = worksRow(row, role, meId) || role === 'Account Executive';
  const subline = [
    row.insured,
    row.carrier ?? 'Carrier not in AMS360',
    row.line,
    row.policyNumber,
  ].join(' | ');
  // The footer's More menu (the QuickView card): its own Esc closes it before the panel.
  const { open: moreOpen, setOpen: setMoreOpen, ref: moreRef } = usePopover();
  // The list's auto refresh goes on while it is open, so the panel shows the row read again.
  useModalLayer(ref, { holdsRefresh: false });
  useEffect(() => {
    // With a file open, Esc closes only the viewer on top (it listens on the document too).
    const onKey = (e: KeyboardEvent) => {
      // A popup over the Quick view (Add note, Reassign) closes on its own Escape, not this panel.
      if (e.key === 'Escape' && !file && !moreOpen && isTopLayer(ref)) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, file, ref, moreOpen]);
  const h = row.handoff ?? null;
  // With the AE: their accepted handoff, worked from Follow up on, Pass and Finish handoff.
  const withMe = role === 'Account Executive' && h?.state === 'accepted' && h.to.id === meId;
  const act = h ? actByChip(h, now) : null;

  const status = statuses[row.status];
  const upcoming = band(row.daysLeft) === 'upcoming' && status.stage === 'open';
  const reason = attentionBanner(row);
  const diff = change(row, rule);

  return (
    <>
      <button type="button" className="scrim" aria-label="Close quick view" onClick={onClose} />
      <aside ref={ref} className="panel" role="dialog" aria-modal="true" aria-labelledby="qv-title">
        <div className="p-head">
          <div className="row g8 wrap">
            <h2 id="qv-title" className="dtitle">
              {row.insured}
            </h2>
            <span className={`pill ${status.pill}`}>{upcoming ? 'Not started' : status.label}</span>
            <AwaitingTag h={row.handoff} meId={meId} />
            {/* On a branch (spec C4, S17): yellow Non-renewal with its date, grey Policy inactive. */}
            {row.branch && (
              <Tip text={branchTag(row.branch).tip} focusable>
                <span className={branchTag(row.branch).className}>
                  {branchTag(row.branch).text}
                </span>
              </Tip>
            )}
            {showIncreaseTag(row, rule) && (
              <Tip text={increaseTooltip(row, rule)} focusable>
                <span className="tag t-amber">{increaseTagText(row)}</span>
              </Tip>
            )}
            <span className={daysPill(row.daysLeft)}>{daysLabel(row.daysLeft)}</span>
          </div>
          <button type="button" className="xbtn" aria-label="Close quick view" onClick={onClose}>
            <XIcon />
          </button>
        </div>
        <div className="p-body">
          {reason && (
            <div className="bnr attn">
              <AlertTriangleIcon />
              <span>{reason}</span>
            </div>
          )}
          {h ? (
            <section className="gbox" aria-label="Handoff">
              <div className="row g8 wrap">
                {reasonLong(h) && <span className={reasonPillCls(h)}>{reasonLong(h)}</span>}
                <span className="t12 text2">{aeBannerTitle(h).from}</span>
                {act && (
                  <span className="ml-auto">
                    <Tip text={act.tip} focusable>
                      <span className={act.cls}>{act.text}</span>
                    </Tip>
                  </span>
                )}
              </div>
              {h.note && <blockquote className="vnote">{h.note}</blockquote>}
            </section>
          ) : (
            row.finishedHandoff && (
              <section className="gbox" aria-label="Handoff">
                <div className="row g8 wrap">
                  {reasonLong(row.finishedHandoff) && (
                    <span className={reasonPillCls(row.finishedHandoff)}>
                      {reasonLong(row.finishedHandoff)}
                    </span>
                  )}
                  <span className="t12 text2">{resultText(row.finishedHandoff, meId)}</span>
                </div>
              </section>
            )
          )}
          <div className="col g6">
            <div className="flab m0">Details</div>
            <Line label="Insured" value={row.insuredFull} />
            <Line label="Policy #" value={row.policyNumber} mono />
            <Line label="Carrier" value={row.carrier ?? 'Not in AMS360'} />
            <Line label="Line" value={row.line} />
            <Line label="Expires" value={longDate(row.expirationDate)} />
            <Line label="Handled by" value={row.handledBy?.name ?? 'Unassigned'} />
            <Line label="Bill method" value={row.agencyBilled ? 'Agency billed' : 'Direct bill'} />
          </div>
          {steps ? (
            <QuickSteps steps={steps} />
          ) : (
            <p className="m0 t12 muted">
              {mine.steps === 'failed' ? 'The steps could not be loaded.' : 'Loading the steps.'}
            </p>
          )}
          <div className="col g6">
            <div className="flab m0">Prior term vs renewal</div>
            <Line label="Prior premium" value={money(row.priorPremium) || 'Not in AMS360'} mono />
            <Line
              label="Renewal premium"
              value={money(row.renewalPremium) || 'Not known yet'}
              mono
            />
            {diff && (
              <div className="row between t13">
                <span className="muted">Change</span>
                <span className={`mono ${diff.cls}`}>{diff.text}</span>
              </div>
            )}
          </div>
          <div className="col g6">
            <div className="flab m0">AMS360</div>
            <Line
              label="Renewed in AMS360"
              value={row.renewedInAmsAt ? `Yes, seen ${when(row.renewedInAmsAt, now)}` : 'Not yet'}
            />
            {row.inactiveInAmsSince && (
              <Line
                label="No longer active in AMS360"
                value={`Since ${when(row.inactiveInAmsSince, now)}`}
              />
            )}
            {upcoming && (
              <p className="m0 t12 muted">Not in a queue yet. It enters the queues at 45 days.</p>
            )}
          </div>
          <section aria-labelledby="qv-n" className="col g8">
            <div className="row between">
              <h3 id="qv-n" className="h3">
                Notes
              </h3>
              <button
                type="button"
                className="linkbtn t12"
                aria-haspopup="dialog"
                onClick={onAddNote}
              >
                Add note
              </button>
            </div>
            {notes.failed ? (
              <p className="m0 t12 muted">The notes could not be loaded.</p>
            ) : notes.view && notes.view.notes.length > 0 ? (
              <div className="qlist">
                {notes.view.notes.map((n) => (
                  <NoteLine key={n.id} n={n} />
                ))}
              </div>
            ) : (
              notes.view && <p className="m0 t12 muted">No notes yet.</p>
            )}
          </section>
          {fetch && fetch.documents.length > 0 && (
            <section aria-labelledby="qv-f" className="col g8">
              <h3 id="qv-f" className="h3">
                Files
              </h3>
              <div className="qlist">
                {fetch.documents.map((d) => (
                  <div key={d.id} className="qr">
                    <FileIcon />
                    <div className="col g2 grow" style={{ minWidth: 0 }}>
                      <span className="t13 ink ell">{d.fileName}</span>
                      <span className="t11 muted">
                        {docLabel(d)}, {decStamp(d, fetch.carrier?.name ?? row.carrier)}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm"
                      aria-haspopup="dialog"
                      onClick={() =>
                        setFile({
                          title: d.fileName,
                          subline,
                          src: api.documentUrl(d.id),
                          downloadHref: api.documentUrl(d.id, true),
                          kind: 'pdf',
                        })
                      }
                    >
                      View
                    </button>
                    <a className="btn btn-sm" href={api.documentUrl(d.id, true)}>
                      <DownloadIcon className="ico s12" />
                      Download
                    </a>
                  </div>
                ))}
              </div>
            </section>
          )}
          <section aria-labelledby="qv-a" className="col g4">
            <h3 id="qv-a" className="h3">
              Latest activity
            </h3>
            {log ? (
              <div>
                {log.slice(0, 3).map((e, i) => (
                  <LogLine key={i} e={e} className="act" />
                ))}
              </div>
            ) : (
              <p className="m0 t12 muted">
                {mine.log === 'failed' ? 'The activity could not be loaded.' : 'Loading.'}
              </p>
            )}
          </section>
        </div>
        <div className="qfoot">
          {withMe && h && followingUp === row.id ? (
            <FollowUpFooter
              day={h.followUpOn ?? null}
              now={now}
              onCancel={() => setFollowingUp(null)}
              onSave={async (on) => {
                const result = await onFollowUp(h.id, on);
                if (result === true) setFollowingUp(null);
                return result;
              }}
            />
          ) : (
            <div className="p-foot">
              {onSendBack && (
                <button type="button" className="btn" aria-haspopup="dialog" onClick={onSendBack}>
                  <UndoIcon className="ico s14" />
                  Send back
                </button>
              )}
              {(onAccept || withMe) && (
                <div ref={moreRef} className="qmore">
                  <button
                    type="button"
                    className="btn"
                    aria-haspopup="menu"
                    aria-expanded={moreOpen}
                    onClick={() => setMoreOpen(!moreOpen)}
                  >
                    More
                    <ChevronDownIcon className="ico s12 muted" />
                  </button>
                  {moreOpen && (
                    <>
                      <button
                        type="button"
                        className="pop-scrim"
                        aria-label="Close menu"
                        onClick={() => setMoreOpen(false)}
                      />
                      <div className="menu qmenu" role="menu" aria-label="More actions">
                        {withMe && (
                          <button
                            type="button"
                            className="mi"
                            role="menuitem"
                            onClick={() => {
                              setMoreOpen(false);
                              setFollowingUp(row.id);
                            }}
                          >
                            Follow up on
                          </button>
                        )}
                        {onPass && (
                          <button
                            type="button"
                            className="mi"
                            role="menuitem"
                            aria-haspopup="dialog"
                            onClick={() => {
                              setMoreOpen(false);
                              onPass();
                            }}
                          >
                            Pass to another AE
                          </button>
                        )}
                        <button
                          type="button"
                          className="mi"
                          role="menuitem"
                          aria-haspopup="dialog"
                          onClick={() => {
                            setMoreOpen(false);
                            onReassign();
                          }}
                        >
                          Reassign
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
              {/* The AE on a renewal that is not theirs: Take over, then Reassign (the board). */}
              {!onAccept && !withMe && onTakeOver && (
                <button type="button" className="btn" onClick={onTakeOver}>
                  <UserCheckIcon className="ico s14" />
                  Take over
                </button>
              )}
              {!onAccept && !withMe && works && !closed && (
                <button type="button" className="btn" aria-haspopup="dialog" onClick={onReassign}>
                  <SwapIcon className="ico s14" />
                  Reassign
                </button>
              )}
              {offersHand(row, role, meId) && (
                <button type="button" className="btn" aria-haspopup="dialog" onClick={onHand}>
                  <SendIcon className="ico s14" />
                  Hand to AE
                </button>
              )}
              <span className="ml-auto" />
              <Link
                className={onAccept || withMe ? 'btn' : 'btn btn-primary'}
                to={`/records/${row.id}`}
                state={{ from: page, tab }}
              >
                Open record
              </Link>
              {onAccept && (
                <button type="button" className="btn btn-primary" onClick={onAccept}>
                  <CheckIcon className="ico s14" />
                  Accept
                </button>
              )}
              {withMe && onFinish && (
                <button
                  type="button"
                  className="btn btn-primary"
                  aria-haspopup="dialog"
                  disabled={!steps}
                  onClick={() => steps && onFinish(steps)}
                >
                  Finish handoff
                </button>
              )}
            </div>
          )}
        </div>
      </aside>
      {file && <Viewer {...file} above onClose={() => setFile(null)} />}
    </>
  );
}
